import { useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { requestPasswordReset } from '@/features/auth/api'
import { AuthInfoPanel } from '@/features/auth/AuthInfoPanel'
import { Recaptcha, type RecaptchaHandle } from '@/features/auth/Recaptcha'
import { ApiError } from '@/api/client'
import logo from '@/assets/vision_controller_logo.jpg'

/**
 * Ports components/user/user_login.html's two-column layout (form card +
 * "Vision Portal" info card) - same headline, field labels, and "Reset
 * password" trigger next to the submit button, matching the legacy page's
 * visual/UX source of truth. The legacy form's RecaptchaField (real,
 * active site/secret keys - see Recaptcha.tsx) is restored below; it was
 * dropped during the initial React migration on the mistaken belief that
 * recaptcha was never actually configured.
 */
export default function LoginPage() {
  const { isAuthenticated, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const recaptchaRef = useRef<RecaptchaHandle>(null)

  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetMessage, setResetMessage] = useState<string | null>(null)
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetSubmitting, setResetSubmitting] = useState(false)

  if (isAuthenticated) {
    const redirectTo = (location.state as { from?: string } | null)?.from ?? '/'
    return <Navigate to={redirectTo} replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA challenge.')
      return
    }
    setError(null)
    setSubmitting(true)
    const result = await login(email, password, recaptchaToken)
    setSubmitting(false)
    if (result.ok) {
      navigate('/', { replace: true })
    } else {
      setError(result.message)
      // A reCAPTCHA token is single-use and expires - always clear it after
      // a failed attempt so the next submit can't silently reuse a stale one.
      recaptchaRef.current?.reset()
      setRecaptchaToken(null)
    }
  }

  async function handleForgotPasswordSubmit(e: FormEvent) {
    e.preventDefault()
    setResetError(null)
    setResetMessage(null)
    setResetSubmitting(true)
    try {
      const res = await requestPasswordReset(resetEmail)
      setResetMessage(res.message)
    } catch (err) {
      setResetError(err instanceof ApiError ? err.message : 'An error occurred. Please try again.')
    } finally {
      setResetSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-secondary px-4 py-12">
      <div className="grid w-full max-w-3xl grid-cols-1 gap-6 rounded-lg border border-border bg-card p-6 shadow-sm md:grid-cols-2 md:p-8">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <img src={logo} alt="Vision Portal" className="h-8 w-8 object-contain" />
            <h1 className="text-2xl font-bold text-brand">Vision Portal</h1>
          </div>
          <h2 className="mb-6 text-xl font-light text-text-primary">Login</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-sm text-text-primary">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded border-2 border-brand bg-input px-3 py-2 text-text-primary outline-none"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-sm text-text-primary">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded border-2 border-brand bg-input px-3 py-2 text-text-primary outline-none"
              />
            </div>

            <Recaptcha ref={recaptchaRef} onChange={setRecaptchaToken} />

            {error && <p className="text-sm italic text-error">{error}</p>}

            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={submitting || !recaptchaToken}
                className="rounded bg-brand px-6 py-2 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Signing in…' : 'Login'}
              </button>
              <button
                type="button"
                onClick={() => setShowForgotPassword((v) => !v)}
                className="text-sm text-brand hover:underline"
              >
                Reset password
              </button>
            </div>
          </form>

          {showForgotPassword && (
            <form onSubmit={handleForgotPasswordSubmit} className="mt-6 space-y-3 border-t border-border pt-4">
              <p className="text-sm text-text-secondary">
                Enter your registered email and we&apos;ll send you a password reset link.
              </p>
              <input
                type="email"
                required
                placeholder="Email address"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full rounded border-2 border-brand bg-input px-3 py-2 text-text-primary outline-none"
              />
              {resetError && <p className="text-sm italic text-error">{resetError}</p>}
              {resetMessage && <p className="text-sm text-brand">{resetMessage}</p>}
              <button
                type="submit"
                disabled={resetSubmitting}
                className="w-full rounded border border-brand py-2 font-semibold text-brand transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {resetSubmitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-text-secondary">
            Need an account?{' '}
            <Link to="/signup" className="text-brand hover:underline">
              Sign up
            </Link>
          </p>
        </div>

        <AuthInfoPanel />
      </div>
    </div>
  )
}
